import dayjs from '../dayjs';
import {Model} from './busRoute';
import {capitalize, cloneDeep, differenceWith, flatten, keyBy, last, memoize, sortBy} from 'lodash';
import {ulid} from 'ulid';
import log from 'loglevel';
import {values} from 'lodash/object';
import {normaliseDate} from '../libs/routes-lib';

const logger = log.getLogger('Schedule');

export const schedule_options = schedules => {
    return (Array.isArray(schedules) ? schedules : values(schedules)).map(schedule => ({
        label: (schedule.isFuture() ? 'Future: ' : '') + schedule.scheduleName,
        value: schedule.scheduleId
    }));
};

export const DATE_STRING = 'DD/MM/YYYY';
const checkDayjs = memoize((date) => {
    const dty = typeof date;
    if ('string' === typeof date) {
        date = dayjs(date, DATE_STRING);
    }
    return date;
});

export const OPERATING_DAY_DEFAULTS = {
    Everyday: null,
    Weekdays: [true, true, true, true, true, false, false],
    Weekends: [false, false, false, false, false, true, true]
};

export class OperatingDays {

    static from(data) {
        return new OperatingDays(data);
    }

    constructor(data) {
        this.monday = true;
        this.tuesday = true;
        this.wednesday = true;
        this.thursday = true;
        this.friday = true;
        this.saturday = true;
        this.sunday = true;
        if (Array.isArray(data)) {
            ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].forEach((day, index) => {
                this[day] = !!data[index];
            });
        } else {
            Object.assign(this, data);
        }
    }

    isMonToFriOnly() {
        return this.isMonToFri() && !this.saturday && !this.sunday;
    }

    isMonToFri() {
        return this.monday && this.tuesday && this.wednesday && this.thursday && this.friday;
    }

    containsWeekday() {
        return this.monday || this.tuesday || this.wednesday || this.thursday || this.friday;
    }

    isRunningOn(date = dayjs()) {
        date = checkDayjs(date);
        return this.toArray()[date.isoWeekday() - 1];
    }

    numberOfDays() {
        return this.toArray().filter(Boolean).length;
    }

    toArray() {
        return [this.monday, this.tuesday, this.wednesday, this.thursday, this.friday, this.saturday, this.sunday];
    }

    toString() {
        if (this.monday && this.tuesday && this.wednesday && this.thursday && this.friday) {
            if (this.saturday && this.sunday) {
                return 'Everyday';
            }
            return 'Weekdays' + (this.saturday ? ' and Saturday' : this.sunday ? ' and Sunday' : '');
        } else if (!this.monday && !this.tuesday && !this.wednesday && !this.thursday && !this.friday && this.saturday && this.sunday) {
            return 'Weekends';
            // } else if (Object.keys(this).reduce((prev, val) => !!this[val] ? prev++ : prev, 0) === 1) {
            //     switch (this.count) {
            //         case 0:
            //             return 'Mondays only'
            //         case 1:
            //             return 'Tuesdays only'
            //         case 2:
            //             return 'Wednesdays only'
            //         case 3:
            //             return 'Thursdays only'
            //         case 4:
            //             return 'Fridays only'
            //         case 5:
            //             return 'Saturdays only'
            //         case 6:
            //             return 'Sundays only'
            //     }
        } else if (this.toArray().every(d => !d)) {
            return '';
        } else {
            return Object.keys(this).filter(d => !!this[d]).map((d, idx) => capitalize(d) + 's').join(', ') + ' only';
        }
    }

    isEqual(operatingDays) {
        if (!operatingDays) {
            return false;
        }
        return this.monday === operatingDays.monday && this.tuesday === operatingDays.tuesday &&
            this.wednesday === operatingDays.wednesday && this.thursday === operatingDays.thursday &&
            this.friday === operatingDays.friday && this.saturday === operatingDays.saturday &&
            this.sunday === operatingDays.sunday;
    }
}

export class Period {

    constructor(data) {
        this.id = data?.id || ulid();
        this.start = null;
        this.end = null;
        this.type = 'Period';
        this.forceInclude = false;
        this.name = data?.name;
        this.shortName = data?.shortName;
        this.update(data);
    }

    isSingleDay() {
        return this.start?.diff(this.end, 'day') === 0;
    }

    isValid() {
        return dayjs.isDayjs(this.start) && dayjs.isDayjs(this.end) && this.start.isValid() && this.end.isValid() && this.end.isSameOrAfter(this.start);
    }

    toString() {
        return !this.isValid() ? 'Invalid' : this.isSingleDay() ? this.start.format(DATE_STRING) : this.start.format(DATE_STRING) + '→' + this.end.format(DATE_STRING);
    }

    update(data) {
        this.start = null;
        this.end = null;
        if (data) {
            if (Array.isArray(data)) {
                this.start = dayjs.isDayjs(data[0]) ? data[0] : normaliseDate(data[0]);
                this.end = dayjs.isDayjs(data[1]) ? data[1] : normaliseDate(data[1]);
                if (data.length === 3 && data[2] === 'forceInclude') {
                    this.forceInclude = true;
                }
            } else if (data.start && data.end) {
                this.start = dayjs.isDayjs(data.start) ? data.start : normaliseDate(data.start);
                this.end = dayjs.isDayjs(data.end) ? data.end : normaliseDate(data.end);
                // } else {
                //     logger.debug('the data', data)
                //     throw new Error('Period is invalid: ', data);
            }
            if (!this.isValid()) {
                this.start = null;
                this.end = null;
            }
        }
    }

    contains(date = dayjs()) {
        date = checkDayjs(date);
        return date.isBetween(this.start, this.end, 'day', '[]');
    }

    toArray() {
        return [this.start ? this.start.toDate() : null, this.end ? this.end.toDate() : null];
    }

    toJson() {
        const array = [this.start ? this.start.format(DATE_STRING) : null, this.end ? this.end.format(DATE_STRING) : null];
        if (this.forceInclude) {
            array.push('forceInclude');
        }
        return array;
    }

    totalDays() {
        return this.forEachDate().length;
    }

    toDays() {
        return this.forEachDate();
    }

    forEachDate() {
        const dates = [];
        for (let m = this.start.clone(); m.diff(this.end, 'days') <= 0; m = m.add(1, 'days')) {
            dates.push(m);
        }
        return dates;
    }

    isEqual(period) {
        if (!period) {
            return false;
        }
        return this.start.isSame(period.start) && this.end.isSame(period.end);
    }
}

// const SCHEDULE_INCLUSION_MAP = {ignore: 'Ignore', include: 'Include', exclude: 'Exclude'}
const periodSorter = (p1, p2) => p1.start.isBefore(p2.start) ? -1 : 1;

export class Exception {
    constructor(data) {
        this.id = data?.id || ulid();
        this.type = data?.type || 'exclude'; // 'include' or 'exclude'
        this.period = data?.period ? new Period(data?.period) : undefined;
        this.scheduleId = data?.scheduleId;
        this.source = data?.source;
    }

    isValid() {
        // Valid if either a valid period or a valid scheduleId (and it exists in PREDEFINED_SCHEDULES)
        if (this.scheduleId) {
            return !!PREDEFINED_SCHEDULES[this.scheduleId];
        }
        return ['include', 'exclude'].includes(this.type) && this.period && this.period.isValid();
    }

    contains(date) {
        if (this.scheduleId && PREDEFINED_SCHEDULES[this.scheduleId]) {
            // Check if date is in any period of the referenced schedule
            return PREDEFINED_SCHEDULES[this.scheduleId].schedulePeriods.some(period => period.contains(date));
        }
        return this.period && this.period.contains(date);
    }

    toJson() {
        return {
            id: this.id,
            type: this.type,
            period: this.period ? this.period.toJson() : undefined,
            scheduleId: this.scheduleId,
            source: this.source,
        };
    }
}

export class Schedule extends Model {
    static optimiseMultiple = (schedules) => {
        return optimiseSchedules(schedules);
    }

    constructor(data) {
        super();
        // this.startDate = null // The inclusive first day of the schedule, defaults to today
        // this.endDate = null // The exclusive last day of the schedule, null means no end date
        // this.occurrences = 0 // End after occurrences
        // this.startTime = 0 // The start time of a trip in seconds since midnight
        // this.endTime = 0 // The end time of a trip in seconds since midnight, defaults to route.lastStop.delta
        // this.recurring = null // daily, weekly, etc, or number indicating "every num days"
        // data?.excludedPeriods && logger.debug([...data.excludedPeriods])

        this.scheduleId = '_';
        this.scheduleName = null;
        this.schedulePeriods = []; // [[Period,Period]]
        this.operatingDays = new OperatingDays([]);
        this.excludedPeriods = []; // [[Period,Period]]
        this.includedSchedules = []; // [Schedule]
        this.excludedSchedules = []; // [Schedule]
        this.charter = false;
        this.exceptions = [];
        this.tags = data?.tags || [];
        this.shared = data?.shared || false;
        // this.publicHolidays = HOLIDAY_MAP.ignore;
        // this.schoolYear = HOLIDAY_MAP.ignore;
        // this.schoolTerms = HOLIDAY_MAP.ignore;
        // this.schoolHolidays = HOLIDAY_MAP.ignore;

        Object.assign(this, data);
        if ('string' === typeof this.operatingDays) {
            switch (this.operatingDays) {
                case 'WD':
                    this.operatingDays = new OperatingDays([true, true, true, true, true, false, false]);
                    break;
                case 'SAT':
                    this.operatingDays = new OperatingDays([false, false, false, false, false, true, false]);
                    break;
                case 'SUN':
                    this.operatingDays = new OperatingDays([false, false, false, false, false, false, true]);
                    break;
                default:
            }
        } else {
            this.operatingDays = new OperatingDays(this.operatingDays);
        }
        if (!this.schedulePeriods?.length && this.schedulePeriod?.length) {
            this.schedulePeriods = [this.schedulePeriod];
            delete this.schedulePeriod;
        }
        if (!this.excludedPeriods && this.excludedPeriod) {
            this.excludedPeriods = [this.excludedPeriod];
            delete this.excludedPeriod;
        }
        if (this.schedulePeriods.length && !Array.isArray(this.schedulePeriods[0]) && !(this.schedulePeriods[0] instanceof Period)) {
            this.schedulePeriods = [this.schedulePeriods];
        }
        if (this.excludedPeriods.length && !Array.isArray(this.excludedPeriods[0]) && !(this.excludedPeriods[0] instanceof Period)) {
            this.excludedPeriods = [this.excludedPeriods];
        }
        this.schedulePeriods = this.schedulePeriods.map(period => new Period(period)).filter(period => period && period.isValid());
        this.excludedPeriods = this.excludedPeriods.map(period => new Period(period)).filter(period => period && period.isValid());
        this.exceptions = (data?.exceptions || []).map(exception => new Exception(exception)).filter(exception => exception.isValid());
        // Migrate excludedPeriods to exceptions (legacy)
        if (this.excludedPeriods?.length) {
            const legacyExclusions = this.excludedPeriods.map(period => {
                const ex = new Exception({ type: 'exclude', period });
                ex.legacy = true;
                return ex;
            });
            this.exceptions = [...legacyExclusions, ...this.exceptions];
        }

        this.schedulePeriods.sort((d1, d2) => d1.start.unix() - d2.start.unix());

        if (this.scheduleId === this.scheduleName) {
            this.scheduleName = this.schedulePeriods.map(period => period.toString()).join(', ');
        }
    }

    static clone(data) {
        return new Schedule(cloneDeep(data));
    }

    setSubSchedules(schedules) {
        const log = this.scheduleName === 'My School year';
        log && logger.debug('Setting subSchedules...', {...this});
        log && logger.debug(PREDEFINED_SCHEDULES);
        this.includedSchedules = this.includedSchedules.map(sId => typeof sId === 'string' ? (PREDEFINED_SCHEDULES[sId] || schedules[sId]) : sId).filter(s => s !== this && s?.setSubSchedules);
        this.includedSchedules.forEach(s => s.setSubSchedules(schedules));
        this.excludedSchedules = this.excludedSchedules.map(sId => {
            logger.debug(PREDEFINED_SCHEDULES[sId], schedules[sId]);
            return typeof sId === 'string' ? (PREDEFINED_SCHEDULES[sId] || schedules[sId]) : sId;
        }).filter(s => s?.setSubSchedules);
        this.excludedSchedules.forEach(s => s.setSubSchedules(schedules));
        log && logger.debug(this.excludedSchedules);
    }

    containsSubSchedule(schedule) {
        return this.includesSubSchedule(schedule) || this.excludesSubSchedule(schedule);
    }

    includesSubSchedule(schedule) {
        return this.includedSchedules.some(includedSchedule => includedSchedule.scheduleId === schedule.scheduleId || includedSchedule.includesSubSchedule(schedule));
    }

    excludesSubSchedule(schedule) {
        return this.excludedSchedules.some(excludedSchedule => excludedSchedule.scheduleId === schedule.scheduleId || excludedSchedule.excludesSubSchedule(schedule));
    }

    totalDays(periods) {
        if (!periods?.length) return 0;
        return periods.reduce((p, c) => c.totalDays() + p, 0);
    }

    getRunningDates(sameOrAfter = dayjs(), maxDays = 20) {
        const scheduledDays = flatten(this.getAllScheduledPeriods().map(p => p.toDays()));
        const excludedDays = flatten(this.getAllExcludedPeriods().map(p => p.toDays()));
        const diffDays = differenceWith(scheduledDays, excludedDays, (date1, date2) => date1.isSame(date2, 'day'))
        let totalScheduledDays = !this.shared ? diffDays.filter(date => this.isOperatingDay(date)) : diffDays;
        
        // Apply exceptions in order - exceptions override everything including operating days
        if (this.exceptions?.length) {
            totalScheduledDays = this.exceptions.reduce((dates, exception) => {
                if (exception.type === 'include') {
                    // Add dates from include exception regardless of operating days
                    let includeDates = [];
                    if (exception.scheduleId && PREDEFINED_SCHEDULES[exception.scheduleId]) {
                        includeDates = flatten(PREDEFINED_SCHEDULES[exception.scheduleId].schedulePeriods.map(p => p.toDays()));
                    } else if (exception.period) {
                        includeDates = exception.period.toDays();
                    }
                    return [...dates, ...includeDates.filter(date => !dates.some(d => d.isSame(date, 'day')))];
                } else {
                    // Remove dates from exclude exception
                    if (exception.scheduleId && PREDEFINED_SCHEDULES[exception.scheduleId]) {
                        const excludeDates = flatten(PREDEFINED_SCHEDULES[exception.scheduleId].schedulePeriods.map(p => p.toDays()));
                        return dates.filter(date => !excludeDates.some(ex => ex.isSame(date, 'day')));
                    } else if (exception.period) {
                        return dates.filter(date => !exception.contains(date));
                    }
                    return dates;
                }
            }, totalScheduledDays);
        }

        return totalScheduledDays.filter(date => date.isSameOrAfter(sameOrAfter, 'day')).slice(0, maxDays);
    }


    getAllScheduledPeriods(sort) {
        let periods = (this.schedulePeriods || [])
            .concat(flatten(this.includedSchedules?.map(s => s?.getAllScheduledPeriods ? s.getAllScheduledPeriods() : []) || []))
            .concat(flatten(this.excludedSchedules?.map(s => s?.getAllExcludedPeriods ? s.getAllExcludedPeriods() : []) || []));
        sort && periods.sort((p1, p2) => p1.start.isBefore(p2.start) ? -1 : 1);
        return periods;
    }

    getAllExcludedPeriods() {
        return (this.excludedPeriods || [])
            .concat(flatten(this.excludedSchedules?.map(s => s?.getAllScheduledPeriods ? s.getAllScheduledPeriods() : []) || []))
            .concat(flatten(this.includedSchedules?.map(s => s?.getAllExcludedPeriods ? s.getAllExcludedPeriods() : []) || []));
    }

    hasExclusions() {
        return this.getAllExcludedPeriods().length;
    }

    exclusionsToString() {
        return this.getAllExcludedPeriods().map(p => p.toString()).join(', ');
    }

    // TODO: Do operatingDays change with included/excluded Schedules
    isOperatingDay(date = dayjs()) {
        return this.operatingDays.isRunningOn(date);
    }

    isScheduled(date = dayjs(), specificPeriod = true) {
        // Check exceptions first - they have highest precedence
        const matchingException = this.exceptions?.find(exception => exception.contains(date));
        if (matchingException) {
            return matchingException.type === 'include';
        }

        if (specificPeriod) {
            return this.getAllScheduledPeriods().some(schedulePeriod => schedulePeriod.contains(date));
        }
        return this.getFirstActiveDate().isSameOrBefore(date) && this.getLastActiveDate().isSameOrAfter(date);
    }

    isExcluded(date = dayjs()) {
        // Check exceptions first - they have highest precedence
        const matchingException = this.exceptions?.find(exception => exception.contains(date));
        if (matchingException) {
            return matchingException.type === 'exclude';
        }

        return this.getAllExcludedPeriods().some(excludedPeriod => excludedPeriod.contains(date));
    }

    isForceIncluded(date = dayjs()) {
        return this.getAllScheduledPeriods().some(period => period.forceInclude && period.contains(date));
    }

    isActive(date = dayjs(), checkOperatingDay = true) {
        // Check exceptions first - they have highest precedence
        const matchingException = this.exceptions?.find(exception => exception.contains(date));
        if (matchingException) {
            return matchingException.type === 'include';
        }

        const scheduled = checkOperatingDay ? this.isScheduled(date, true) : !this.isObsolete(date);
        const excluded = checkOperatingDay && this.isExcluded(date);
        const isOperatingToday = checkOperatingDay ? this.isOperatingDay(date) : this.isSchoolSchedule();
        const forceIncluded = checkOperatingDay && this.isForceIncluded(date);
        // logger.debug('%s Scheduled: %s, Excluded: %s, Operating Today: %s', this.scheduleName, scheduled, excluded, isOperatingToday)
        return scheduled && !excluded && (isOperatingToday || forceIncluded);
    }

    isActiveToday(date = dayjs()) {
        return this.isActive(date);
    }

    isMonToFriOnly() {
        return this.operatingDays.isMonToFriOnly();
    }

    isMonToFri() {
        return this.operatingDays.isMonToFri();
    }

    containsWeekday() {
        return this.operatingDays.containsWeekday();
    }

    isSchoolSchedule() {
        return this.containsWeekday();
    }

    getFirstActiveDate() {
        if (!this.getAllScheduledPeriods()?.length) {
            return null;
        }
        return this.getAllScheduledPeriods(true)[0].start;
    }

    getLastActiveDate() {
        if (!this.getAllScheduledPeriods()?.length) {
            return null;
        }
        return last(this.getAllScheduledPeriods(true)).end;
    }

    includeSchoolHolidays() {
        this.includedPeriods = this.includedPeriods.concat(SCHOOL_HOLIDAYS);
    }

    includePublicHolidays() {
        this.includedPeriods = this.includedPeriods.concat(PUBLIC_HOLIDAY_PERIODS);
    }

    excludeSchoolHolidays() {
        // SCHOOL_HOLIDAYS.forEach(holidayPeriod => {
        // if (this.isScheduled(holidayPeriod[0]) && this.isScheduled(holidayPeriod[1])) {
        //     this.excludedPeriods = this.excludedPeriods.concat([holidayPeriod]);
        // }
        // })
        this.excludedPeriods = this.excludedPeriods.concat(SCHOOL_HOLIDAYS);
    }

    excludePublicHolidays() {
        // this.excludedPeriods = this.excludedPeriods.concat(Object.keys(PUBLIC_HOLIDAYS).filter(date => this.isScheduled(date)).map(date => [date, date]));
        this.excludedPeriods = this.excludedPeriods.concat(PUBLIC_HOLIDAY_PERIODS);
    }

    getNextDepartureDates({date = dayjs(), count = 60} = {}) {
        return this.getRunningDates(date).slice(0, count)
    }
        

    getNext7DepartureDates(date = dayjs()) {
        let next = this.getNextDepartureDate(date, 70);
        const result = [];
        for (let i = 0; i < 70; i++) {
            if (next) {
                result.push(next);
                next = this.getNextDepartureDate(next.clone().add(1, 'day'));
            }
        }
        return result;
    }

    getNextDepartureDate(date = dayjs()) {
        const log = false;//this.scheduleName.startsWith('Predefined')
        date = date.clone().startOf('d');
        let attempts = 0;
        log && logger.debug('checking date', date.format(DATE_STRING));
        while (attempts++ < 70 && !this.isActive(date)) {
            log && logger.debug('checking date again', date.format(DATE_STRING));
            date = date.add(1, 'day');
        }
        log && logger.debug(date.format(DATE_STRING));
        if (this.isActive(date)) {
            return date;
        }
    }

    toJson() {
        const schedulePeriodsJson = this.schedulePeriods.filter(p => p.isValid()).map(period => period.toJson());
        const excludedPeriodsJson = this.excludedPeriods.filter(p => p.isValid()).map(period => period.toJson());
        const operatingDaysJson = this.operatingDays.toArray();
        const exceptionsJson = this.exceptions.filter(e => e.isValid()).map(exception => exception.toJson());
        return {
            scheduleId: this.scheduleId,
            scheduleName: this.scheduleName,
            schedulePeriods: schedulePeriodsJson,
            excludedPeriods: excludedPeriodsJson,
            operatingDays: operatingDaysJson,
            includedSchedules: this.includedSchedules.map(s => typeof s === 'string' ? s : s.scheduleId),
            excludedSchedules: this.excludedSchedules.map(s => typeof s === 'string' ? s : s.scheduleId),
            userId: this.userId,
            charter: this.charter,
            exceptions: exceptionsJson,
            tags: this.tags,
            shared: this.shared
        };
    }

    isFuture(date = dayjs()) {
        const firstActive = this.getFirstActiveDate();
        if (!firstActive) {
            return false;
        }
        return date.isBefore(firstActive);
    }

    isObsolete(date = dayjs()) {
        // This was commented out to "Fix obsolete schedules in transfers"
        // I'm adding it back so that we can determine if a schedule is obsolete to fix an issue where transfers are showing no schedule because school runs aren't scheduled for next year.
        const lastActive = this.getLastActiveDate();
        if (!lastActive) {
            return true;
        }
        return date.isAfter(lastActive, 'day');
    }

    hasScheduleId(scheduleId) {
        // Check current schedule
        if (this.scheduleId === scheduleId) {
            return true;
        }

        // Check included schedules recursively
        if (this.includedSchedules?.length) {
            return this.includedSchedules.some(schedule => 
                schedule.hasScheduleId(scheduleId)
            );
        }

        // Check optimised schedules recursively
        if (this.optimisedSchedules?.length) {
            return this.optimisedSchedules.some(schedule => 
                schedule.hasScheduleId(scheduleId)
            );
        }
        return false;
    }

    isSame(schedule) {
        if (!schedule) {
            return false;
        }
        return this.operatingDays.isEqual(schedule.operatingDays) &&
            this.schedulePeriods.sort(periodSorter).every((period, idx) => period.isEqual(schedule.schedulePeriods.sort(periodSorter)[idx])) &&
            this.excludedPeriods.sort(periodSorter).every((period, idx) => period.isEqual(schedule.excludedPeriods.sort(periodSorter)[idx])) &&
            sortBy(this.includedSchedules, 'scheduleId').every((s, idx) => s.isEqual(sortBy(schedule.includedSchedules, 'scheduleId')[idx])) &&
            sortBy(this.excludedSchedules, 'scheduleId').every((s, idx) => s.isEqual(sortBy(schedule.excludedSchedules, 'scheduleId')[idx])) &&
            this.exceptions.sort((a, b) => a.id.localeCompare(b.id)).every((exception, idx) => 
                exception.type === schedule.exceptions.sort((a, b) => a.id.localeCompare(b.id))[idx]?.type &&
                exception.period.isEqual(schedule.exceptions.sort((a, b) => a.id.localeCompare(b.id))[idx]?.period)
            );
    }

    combineAdjacentPeriods() {
        this.schedulePeriods = combineAdjacentPeriods(this.schedulePeriods);
        this.excludedPeriods = combineAdjacentPeriods(this.excludedPeriods);
    }
}


// NOTE: Ensure dates are in the format dd/mm/yyyy
export const PUBLIC_HOLIDAYS = {
    2023: '01/01/2023,New Year\'s Day;02/01/2023,Additional Day;26/01/2023,Australia Day;07/04/2023,Good Friday;08/04/2023,Easter Saturday;09/04/2023,Easter Sunday;10/04/2023,Easter Monday;25/04/2023,Anzac Day;12/06/2023,Queen\'s Birthday;2/10/2023,Labour Day;25/12/2023,Christmas Day;26/12/2023,Boxing Day',
    2024: '01/01/2024, New Year\'s Day; 26/01/2024, Australia Day; 29/03/2024, Good Friday; 30/03/2024, Easter Saturday; 31/03/2024, Easter Sunday; 01/04/2024, Easter Monday; 25/04/2024, Anzac Day; 10/06/2024, King\'s Birthday; 07/10/2024, Labour Day; 25/12/2024, Christmas Day; 26/12/2024, Boxing Day',
    2025: '01/01/2025, New Year\'s Day; 27/01/2025, Australia Day; 18/04/2025, Good Friday; 19/04/2025, Easter Saturday; 20/04/2025, Easter Sunday; 21/04/2025, Easter Monday; 25/04/2025, Anzac Day; 09/06/2025, King\'s Birthday; 06/10/2025, Labour Day; 25/12/2025, Christmas Day; 26/12/2025, Boxing Day'
};

export const PUBLIC_HOLIDAY_PERIODS = {
    2023: [],
    2024: [],
    2025: []
};

Object.keys(PUBLIC_HOLIDAYS).forEach(year => {
    PUBLIC_HOLIDAYS[year].split(';').forEach(hol => {
        const holiday = hol.split(',');
        PUBLIC_HOLIDAY_PERIODS[year].push(new Period([holiday[0], holiday[0]]));
    });
});

const SCHOOL_TERMS_STR = {
    2023: '27/01/2023,06/04/2023;24/04/2023,30/06/2023;17/07/2023,22/09/2023;09/10/2023,19/12/2023',
    2024: '30/01/2024,12/04/2024;29/04/2024,05/07/2024;22/07/2024,27/09/2024;14/10/2024,20/12/2024',
    2025: '31/01/2025,11/04/2025;28/04/2025,04/07/2025;21/07/2025,26/09/2025;13/10/2025,19/12/2025'
};

export const SCHOOL_TERMS = {};
Object.keys(SCHOOL_TERMS_STR).forEach(year => {
    SCHOOL_TERMS[year] = SCHOOL_TERMS_STR[year].split(';').map(term => term.split(','));
});
export const SCHOOL_TERM_PERIODS = {};
export const ALL_FUTURE_TERMS = [];
export const THIS_YEARS_FUTURE_TERMS = [];
export const SCHOOL_YEARS = [];
Object.keys(SCHOOL_TERMS).forEach(year => {
    if (year >= dayjs().year()) {
        SCHOOL_YEARS.push(new Period({
            id: `${year}_school_year`,
            name: `${year} School Year`,
            shortName: `${year}`,
            start: SCHOOL_TERMS[year][0][0],
            end: last(SCHOOL_TERMS[year])[1]
        }));
    }
    SCHOOL_TERMS[year].forEach((term, idx) => {
        SCHOOL_TERM_PERIODS[year] = SCHOOL_TERM_PERIODS[year] || [];
        const period = new Period({start: checkDayjs(term[0]).clone(), end: checkDayjs(term[1]).clone()});
        SCHOOL_TERM_PERIODS[year].push(period);
        if (period.end.isSameOrAfter(dayjs(), 'day')) {
            ALL_FUTURE_TERMS.push(new Period({
                id: `term_${idx + 1}_${year}`,
                start: period.start,
                end: period.end,
                name: `Term ${idx + 1} ${year}`,
                shortName: `T${idx + 1} ${year}`
            }));
            if (parseInt(year) === dayjs().year()) {
                THIS_YEARS_FUTURE_TERMS.push(new Period({
                    id: `term_${idx + 1}`,
                    start: period.start,
                    end: period.end,
                    name: `End of T${idx + 1}`,
                    shortName: `T${idx + 1}`
                }));
            }
        }
    });
});

export const THIS_YEARS_FUTURE_TERMS_BY_ID = keyBy(THIS_YEARS_FUTURE_TERMS, 'id');

// export const SCHOOL_YEAR_PERIOD = new Period([SCHOOL_TERMS[0][0], last(SCHOOL_TERMS)[1]]);

export const SCHOOL_HOLIDAYS = {};
Object.keys(SCHOOL_TERMS).forEach(year => SCHOOL_TERMS[year].forEach((term, idx) => {
    SCHOOL_HOLIDAYS[year] = SCHOOL_HOLIDAYS[year] || [];
    if (idx === 0) {
        SCHOOL_HOLIDAYS[year].push(new Period([checkDayjs(term[0]).clone().startOf('year'), checkDayjs(term[0]).clone().subtract(1, 'd')]));
    }
    if (idx < SCHOOL_TERMS[year].length - 1) {
        SCHOOL_HOLIDAYS[year].push(new Period([checkDayjs(term[1]).clone().add(1, 'd'), checkDayjs(SCHOOL_TERMS[year][idx + 1][0]).clone().subtract(1, 'd')]));
    }
    if (idx === SCHOOL_TERMS[year].length - 1) {
        SCHOOL_HOLIDAYS[year].push(new Period([checkDayjs(term[1]).clone().add(1, 'd'), checkDayjs(term[1]).clone().endOf('year')]));
    }
}));//.filter(hols => !!hols)

export const SCHOOL_HOLIDAY_SCHEDULES = {};
Object.keys(SCHOOL_HOLIDAYS).forEach(year => {
    SCHOOL_HOLIDAY_SCHEDULES[year] = new Schedule({
        shared: true,
        scheduleId: year + '_nsw_school_holidays',
        scheduleName: year + ' NSW School Holidays',
        schedulePeriods: SCHOOL_HOLIDAYS[year]
    });
});

export const PUBLIC_HOLIDAY_SCHEDULES = {};
Object.keys(PUBLIC_HOLIDAY_PERIODS).forEach(year => {
    PUBLIC_HOLIDAY_SCHEDULES[year] = new Schedule({
        shared: true,
        scheduleId: year + '_nsw_public_holidays',
        scheduleName: year + ' NSW Public Holidays',
        schedulePeriods: PUBLIC_HOLIDAY_PERIODS[year]
    });
});
export const SCHOOL_TERMS_SCHEDULES = {};
Object.keys(SCHOOL_TERM_PERIODS).forEach(year => {
    SCHOOL_TERMS_SCHEDULES[year] = new Schedule({
        shared: true,
        scheduleId: year + '_nsw_school_terms',
        scheduleName: year + ' NSW School Terms',
        schedulePeriods: SCHOOL_TERM_PERIODS[year]
    });
});
// export const SCHOOL_TERM_SCHEDULE = new Schedule({
//     shared: true,
//     scheduleId: 'nsw_school_term',
//     scheduleName: 'NSW School Term',
//     schedulePeriods: [SCHOOL_TERM_PERIODS.filter(p => p.contains())[0]]
// });
// export const SCHOOL_YEAR_SCHEDULE = new Schedule({
//     shared: true,
//     scheduleId: 'nsw_school_year',
//     scheduleName: 'NSW School Year',
//     schedulePeriods: [SCHOOL_YEAR_PERIOD]
// });
const PREDEFINED_SCHEDULES = {
    // nsw_school_year: new Schedule({shared: true, scheduleName: 'year'}),
    // nsw_school_terms: new Schedule({shared: true, scheduleName: 'terms'}),
    // nsw_school_term: new Schedule({shared: true, scheduleName: 'term'}),
    // nsw_school_holidays: new Schedule({shared: true, scheduleName: 'pub'}),
    // nsw_public_holidays: new Schedule({shared: true, scheduleName: 'hols'}),
};
Object.keys(SCHOOL_TERMS_SCHEDULES).forEach(year => PREDEFINED_SCHEDULES[year + '_nsw_school_terms'] = SCHOOL_TERMS_SCHEDULES[year]);
Object.keys(SCHOOL_HOLIDAY_SCHEDULES).forEach(year => PREDEFINED_SCHEDULES[year + '_nsw_school_holidays'] = SCHOOL_HOLIDAY_SCHEDULES[year]);
Object.keys(PUBLIC_HOLIDAY_SCHEDULES).forEach(year => PREDEFINED_SCHEDULES[year + '_nsw_public_holidays'] = PUBLIC_HOLIDAY_SCHEDULES[year]);

export {PREDEFINED_SCHEDULES};

// SCHOOL_TERM_PERIODS.forEach((period, idx) => {
//     const termNumber = idx+1
//     const scheduleId = `nsw_school_term_${termNumber}`
//     PREDEFINED_SCHEDULE[scheduleId] = new Schedule({
//         scheduleId,
//         scheduleName: `NSW School Term ${termNumber}`,
//         schedulePeriods: [SCHOOL_TERM_PERIODS[idx]]
//     });
// })

export function convertOperatingDaysToSchedules(operatingDays) {
    if (!operatingDays?.length) {
        console.log('No operating days to convert');
        return [];
    }
    
    console.log(`Converting ${operatingDays.length} operating days to schedules`);
    
    return operatingDays.map(operatingDay => {
        console.log(`Processing operating day: ${operatingDay.Id} (${operatingDay.Name})`);
        const schedule = new Schedule({
            scheduleId: operatingDay.Id,
            scheduleName: operatingDay.Name,
            schedulePeriods: [],
            operatingDays: new OperatingDays([]),
            excludedPeriods: [],
            includedSchedules: [],
            excludedSchedules: []
        });

        // Process OperatingCalendar elements
        if (operatingDay.OperatingCalendar) {
            const calendars = Array.isArray(operatingDay.OperatingCalendar) 
                ? operatingDay.OperatingCalendar 
                : [operatingDay.OperatingCalendar];

            console.log(`Processing ${calendars.length} calendar(s) for operating day ${operatingDay.Id}`);

            calendars.forEach(calendar => {
                if (calendar.Recurrence) {
                    console.log(`Processing Recurrence pattern: ${calendar.Recurrence}`);
                    // Parse recurrence pattern
                    const [dates, month, year, dayOfWeek, weekOfMonth] = calendar.Recurrence.split(' ');
                    
                    // Get date range from calendar if year or month is '*'
                    const useCalendarDates = year === '*' || month === '*';
                    const startDate = useCalendarDates ? dayjs(calendar.StartDate, 'YYYY-MM-DD') : null;
                    const endDate = useCalendarDates ? dayjs(calendar.EndDate, 'YYYY-MM-DD') : null;
                    
                    console.log(`Date range: ${startDate?.format('YYYY-MM-DD')} to ${endDate?.format('YYYY-MM-DD')}`);
                    
                    // Handle specific dates
                    if (dates === '*') {
                        if (useCalendarDates) {
                            console.log('Using calendar date range for all dates');
                            // Use the calendar's date range
                            schedule.schedulePeriods.push(new Period({
                                start: startDate,
                                end: endDate
                            }));
                        } else {
                            console.log(`Using all days in month ${month} of year ${year}`);
                            // '*' means every day in the month
                            const periodStart = dayjs(`${year}-${month}-01`, 'YYYY-MM-DD');
                            const periodEnd = periodStart.endOf('month');
                            schedule.schedulePeriods.push(new Period({
                                start: periodStart,
                                end: periodEnd
                            }));
                        }
                    } else {
                        console.log(`Processing specific dates: ${dates}`);
                        const dateList = dates.split(',').map(d => {
                            if (d.includes('-')) {
                                const [start, end] = d.split('-').map(Number);
                                return Array.from({length: end - start + 1}, (_, i) => start + i);
                            }
                            return Number(d);
                        }).flat();

                        console.log(`Expanded date list: ${dateList.join(', ')}`);

                        // Create periods for each date
                        dateList.forEach(date => {
                            let periodStart;
                            if (useCalendarDates) {
                                // For each date in the list, create a period within the calendar's date range
                                periodStart = dayjs(calendar.StartDate, 'YYYY-MM-DD').add(date - 1, 'day');
                                if (periodStart.isAfter(endDate)) {
                                    console.log(`Skipping date ${date} as it's outside calendar range`);
                                    return;
                                }
                            } else {
                                periodStart = dayjs(`${year}-${month}-${date.toString().padStart(2, '0')}`, 'YYYY-MM-DD');
                            }
                            schedule.schedulePeriods.push(new Period({
                                start: periodStart,
                                end: periodStart
                            }));
                        });
                    }

                    // Handle day of week patterns
                    if (dayOfWeek === '*') {
                        console.log('Setting operating days to all days of week');
                        // '*' means every day of the week
                        schedule.operatingDays = new OperatingDays([true, true, true, true, true, true, true]);
                    } else {
                        console.log(`Processing day of week pattern: ${dayOfWeek}`);
                        const days = dayOfWeek.split(',').map(d => {
                            if (d.includes('-')) {
                                const [start, end] = d.split('-').map(Number);
                                return Array.from({length: end - start + 1}, (_, i) => start + i);
                            }
                            return Number(d);
                        }).flat();
                        
                        console.log(`Expanded days list: ${days.join(', ')}`);
                        
                        // Convert to boolean array (0-6 where 0 is Sunday)
                        const operatingDays = Array(7).fill(false);
                        days.forEach(d => {
                            const index = d === 7 ? 0 : d; // Handle Sunday = 0 or 7
                            operatingDays[index] = true;
                        });
                        
                        schedule.operatingDays = new OperatingDays(operatingDays);
                        console.log(`Set operating days pattern: ${schedule.operatingDays.toArray().join('')}`);
                    }

                    // Handle week of month patterns
                    if (weekOfMonth !== '*') {
                        console.log(`Week of month pattern not implemented: ${weekOfMonth}`);
                    }
                }
                if(calendar.BitPattern) {
                    console.log(`Processing BitPattern of length ${calendar.BitPattern.length}`);
                    // Handle BitPattern
                    const startDate = dayjs(calendar.StartDate, 'YYYY-MM-DD');
                    const endDate = dayjs(calendar.EndDate, 'YYYY-MM-DD');
                    const bitPattern = calendar.BitPattern;
                    
                    // Validate bit pattern length matches date range
                    const expectedLength = endDate.diff(startDate, 'days') + 1;
                    if (bitPattern.length !== expectedLength) {
                        console.warn(`Invalid BitPattern length: expected ${expectedLength}, got ${bitPattern.length}`);
                        return;
                    }
                    
                    // Create periods for each active day (where bit is '1')
                    let activeDays = 0;
                    for (let i = 0; i < bitPattern.length; i++) {
                        if (bitPattern[i] === '1') {
                            activeDays++;
                            const date = startDate.add(i, 'days');
                            schedule.schedulePeriods.push(new Period({
                                start: date,
                                end: date
                            }));
                        }
                    }
                    console.log(`Created ${activeDays} periods from BitPattern`);
                }

                console.log(`Schedule periods: ${schedule.schedulePeriods.map(p => p.toString()).join(', ')}`);
                console.log(`Schedule operating days: ${schedule.operatingDays.toArray().join(',')}`);   
            });

            // Combine adjacent periods to optimise the schedule
            console.log(`Combining adjacent periods for schedule ${schedule.scheduleId}`);
            schedule.combineAdjacentPeriods();
            console.log(`Final period count: ${schedule.schedulePeriods.length}`);
        }

        return schedule;
    });
}

export const combineAdjacentPeriods = (periods) => {
    if (!periods?.length) {
        console.log('No periods to combine');
        return [];
    }
    
    console.log(`Combining ${periods.length} periods`);
    
    // Sort periods by start date
    const sortedPeriods = [...periods].sort((a, b) => a.start.unix() - b.start.unix());
    const combinedPeriods = [];
    let currentPeriod = sortedPeriods[0];

    for (let i = 1; i < sortedPeriods.length; i++) {
        const nextPeriod = sortedPeriods[i];
        
        // Check if periods are adjacent or overlapping
        // We add 1 day to account for adjacent dates (e.g., end of one period is start of next)
        if (currentPeriod.end.add(1, 'day').isSameOrAfter(nextPeriod.start)) {
            console.log(`Combining periods: ${currentPeriod.toString()} and ${nextPeriod.toString()}`);
            // Extend current period if next period ends later
            if (nextPeriod.end.isAfter(currentPeriod.end)) {
                currentPeriod.end = nextPeriod.end;
            }
        } else {
            console.log(`No overlap between periods: ${currentPeriod.toString()} and ${nextPeriod.toString()}`);
            // No overlap, add current period and start new one
            combinedPeriods.push(currentPeriod);
            currentPeriod = nextPeriod;
        }
    }
    
    // Add the last period
    combinedPeriods.push(currentPeriod);
    
    console.log(`Combined into ${combinedPeriods.length} periods`);
    return combinedPeriods;
}

export function optimiseSchedulePeriods(schedule) {
    if (!schedule.schedulePeriods?.length) {
        console.log(`No periods to optimize for schedule ${schedule.scheduleId}`);
        return schedule;
    }

    console.log(`Optimizing periods for schedule ${schedule.scheduleId} (${schedule.scheduleName})`);
    console.log(`Initial period count: ${schedule.schedulePeriods.length}`);

    // Group periods by their operating days pattern
    const periodGroups = new Map();
    
    schedule.schedulePeriods.forEach(period => {
        // Get the operating days pattern as a string for use as a key
        const operatingDaysKey = schedule.operatingDays.toArray().join('');
        
        if (!periodGroups.has(operatingDaysKey)) {
            console.log(`Creating new period group for operating days pattern: ${operatingDaysKey}`);
            periodGroups.set(operatingDaysKey, {
                operatingDays: schedule.operatingDays,
                periods: []
            });
        }
        periodGroups.get(operatingDaysKey).periods.push(period);
    });

    console.log(`Grouped into ${periodGroups.size} operating day patterns`);

    // For each group, combine the periods and create a new schedule
    const optimisedSchedules = Array.from(periodGroups.entries()).map(([key, group]) => {
        console.log(`Processing period group with ${group.periods.length} periods and pattern: ${key}`);
        
        const newSchedule = new Schedule({
            scheduleId: schedule.scheduleId + '_' + key,
            scheduleName: schedule.scheduleName,
            operatingDays: group.operatingDays,
            schedulePeriods: group.periods,
            excludedPeriods: schedule.excludedPeriods,
            includedSchedules: schedule.includedSchedules,
            excludedSchedules: schedule.excludedSchedules
        });

        // Combine adjacent periods within this group
        console.log(`Combining adjacent periods for schedule ${newSchedule.scheduleId}`);
        newSchedule.combineAdjacentPeriods();
        console.log(`After combining: ${newSchedule.schedulePeriods.length} periods`);
        
        return newSchedule;
    });

    // If we only have one schedule after optimization, return it directly
    if (optimisedSchedules.length === 1) {
        console.log(`Single schedule after optimization, returning schedule ${optimisedSchedules[0].scheduleId}`);
        return optimisedSchedules[0];
    }

    console.log(`Creating parent schedule for ${optimisedSchedules.length} optimized schedules`);
    // Otherwise, create a parent schedule that includes all the optimised schedules
    const parentSchedule = new Schedule({
        scheduleId: schedule.scheduleId,
        scheduleName: schedule.scheduleName,
        optimisedSchedules: optimisedSchedules,
        excludedPeriods: schedule.excludedPeriods,
        excludedSchedules: schedule.excludedSchedules
    });

    console.log(`Final optimization complete for schedule ${schedule.scheduleId}`);
    return parentSchedule;
}

export function optimiseSchedules(schedules) {
    if (!schedules?.length) {
        console.log('No schedules to optimize');
        return [];
    }
    
    console.log(`Starting optimization of ${schedules.length} schedules`);
    
    // First optimise each individual schedule
    const optimisedSchedules = schedules.map(schedule => {
        console.log(`Optimizing individual schedule: ${schedule.scheduleId} (${schedule.scheduleName})`);
        return (new Schedule(schedule)).optimise();
    });
    
    console.log(`After individual optimization: ${optimisedSchedules.length} schedules`);
    
    // Group schedules by their operating days pattern
    const scheduleGroups = new Map();
    
    optimisedSchedules.forEach(schedule => {
        // Get the operating days pattern as a string for use as a key
        const operatingDaysKey = schedule.operatingDays.toArray().join('');
        
        if (!scheduleGroups.has(operatingDaysKey)) {
            console.log(`Creating new group for operating days pattern: ${operatingDaysKey}`);
            scheduleGroups.set(operatingDaysKey, {
                operatingDays: schedule.operatingDays,
                schedules: []
            });
        }
        scheduleGroups.get(operatingDaysKey).schedules.push(schedule);
    });

    console.log(`Grouped into ${scheduleGroups.size} operating day patterns`);

    // For each group, try to combine schedules with overlapping periods
    const finalSchedules = Array.from(scheduleGroups.entries()).map(([key, group]) => {
        const { operatingDays, schedules } = group;
        console.log(`Processing group with ${schedules.length} schedules and pattern: ${key}`);
        
        // If only one schedule in group, return it
        if (schedules.length === 1) {
            console.log('Single schedule in group, returning as is');
            return schedules[0];
        }

        // Sort schedules by their first period's start date
        const sortedSchedules = [...schedules].sort((a, b) => {
            const aStart = a.getFirstActiveDate();
            const bStart = b.getFirstActiveDate();
            if (!aStart) return 1;
            if (!bStart) return -1;
            return aStart.unix() - bStart.unix();
        });

        console.log(`Sorted ${sortedSchedules.length} schedules by start date`);

        // Try to combine schedules with overlapping periods
        const combinedSchedules = [];
        let currentSchedule = sortedSchedules[0];

        for (let i = 1; i < sortedSchedules.length; i++) {
            const nextSchedule = sortedSchedules[i];
            
            // Check if periods overlap or are adjacent
            const currentEnd = currentSchedule.getLastActiveDate();
            const nextStart = nextSchedule.getFirstActiveDate();
            
            if (currentEnd && nextStart && 
                currentEnd.add(1, 'day').isSameOrAfter(nextStart)) {
                console.log(`Combining schedules ${currentSchedule.scheduleId} and ${nextSchedule.scheduleId} - periods overlap`);
                // Periods overlap or are adjacent, combine them
                const combinedPeriods = [
                    ...currentSchedule.schedulePeriods,
                    ...nextSchedule.schedulePeriods
                ];
                
                currentSchedule = new Schedule({
                    scheduleId: currentSchedule.scheduleId + '_' + nextSchedule.scheduleId,
                    scheduleName: currentSchedule.scheduleName,
                    operatingDays: currentSchedule.operatingDays,
                    schedulePeriods: combinedPeriods,
                    excludedPeriods: [
                        ...(currentSchedule.excludedPeriods || []),
                        ...(nextSchedule.excludedPeriods || [])
                    ],
                    includedSchedules: [
                        ...(currentSchedule.includedSchedules || []),
                        ...(nextSchedule.includedSchedules || [])
                    ],
                    excludedSchedules: [
                        ...(currentSchedule.excludedSchedules || []),
                        ...(nextSchedule.excludedSchedules || [])
                    ]
                });
                
                // Combine adjacent periods
                currentSchedule.combineAdjacentPeriods();
                console.log(`Created combined schedule with ${currentSchedule.schedulePeriods.length} periods`);
            } else {
                console.log(`Schedules ${currentSchedule.scheduleId} and ${nextSchedule.scheduleId} do not overlap`);
                // No overlap, add current schedule and start new one
                combinedSchedules.push(currentSchedule);
                currentSchedule = nextSchedule;
            }
        }
        
        // Add the last schedule
        combinedSchedules.push(currentSchedule);
        
        // If we only have one schedule after combining, return it
        if (combinedSchedules.length === 1) {
            console.log('Single schedule after combining, returning as is');
            return combinedSchedules[0];
        }
        
        console.log(`Creating parent schedule for ${combinedSchedules.length} combined schedules`);
        // Otherwise, create a parent schedule that includes all the combined schedules
        return new Schedule({
            scheduleId: schedules[0].scheduleId + '_combined',
            scheduleName: schedules[0].scheduleName,
            includedSchedules: combinedSchedules,
            operatingDays: operatingDays
        });
    });

    console.log(`Final optimization complete: ${finalSchedules.length} schedules remaining`);
    return finalSchedules;
}




